import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "../api";

/* ── Program builder (v6) ─────────────────────────────────────────────────────
 * Coach-facing route (/admin/programs) with two jobs:
 *   1. Programs: create/edit multi-workout rotations (Day A/B/C...), each
 *      workout a list of lifts with per-lift sets, reps, and rest. Edits are
 *      wholesale replaces on the server; students on the program pick up
 *      changes on their next app load.
 *   2. Lift library: add lifts and soft-deactivate them. Deactivation only
 *      hides a lift from this builder's picker — existing programs and logged
 *      history keep working.
 * Styling mirrors Admin.jsx (same card/input/button objects).
 */

const EQUIPMENT = ["barbell", "dumbbell", "band", "bodyweight"];
const TRACKS = [["required", "Weight required"], ["optional", "Weight optional (bodyweight+)"], ["none", "No weight"]];
const UNITS = [["reps", "Reps"], ["seconds", "Seconds (timed hold)"]];

const slugify = (s) =>
  String(s).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);

/* ── Lift library ────────────────────────────────────────────────────────── */
function AddLiftForm({ onAdded }) {
  const blank = { name: "", key: "", equipment_type: "dumbbell", tracks_weight: "required", rep_unit: "reps" };
  const [f, setF] = useState(blank);
  const [keyTouched, setKeyTouched] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const setName = (e) => {
    const name = e.target.value;
    setF((s) => ({ ...s, name, key: keyTouched ? s.key : slugify(name) }));
  };

  const submit = async () => {
    setMsg(null); setBusy(true);
    try {
      const { data } = await api.post("/admin/lifts", f);
      setMsg({ ok: true, text: `Added ${data.name}` });
      setF(blank); setKeyTouched(false);
      onAdded();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e, "Could not add the lift") });
    } finally { setBusy(false); }
  };

  return (
    <div className="llh-section-sep">
      <div className="llh-grid-2 llh-mb-10">
        <div>
          <label className="llh-label">Lift name</label>
          <input className="llh-input" value={f.name} onChange={setName} placeholder="e.g. Goblet Squat" />
        </div>
        <div>
          <label className="llh-label">Key (permanent, used in logs)</label>
          <input className="llh-input" value={f.key} autoCapitalize="none"
            onChange={(e) => { setKeyTouched(true); setF((s) => ({ ...s, key: slugify(e.target.value) })); }} />
        </div>
        <div>
          <label className="llh-label">Equipment</label>
          <select className="llh-input llh-h-41" value={f.equipment_type} onChange={(e) => setF((s) => ({ ...s, equipment_type: e.target.value }))}>
            {EQUIPMENT.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="llh-label">Weight tracking</label>
          <select className="llh-input llh-h-41" value={f.tracks_weight} onChange={(e) => setF((s) => ({ ...s, tracks_weight: e.target.value }))}>
            {TRACKS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="llh-label">Counted in</label>
          <select className="llh-input llh-h-41" value={f.rep_unit} onChange={(e) => setF((s) => ({ ...s, rep_unit: e.target.value }))}>
            {UNITS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </div>
      </div>
      <div className="llh-row-center">
        <button className="llh-btn-primary llh-btn--dim6" disabled={busy || !f.name.trim() || !f.key} onClick={submit}>
          {busy ? "Adding..." : "Add lift"}
        </button>
        {msg && <span className={msg.ok ? "llh-fs-13 llh-c-done" : "llh-fs-13 llh-c-crimson"}>{msg.text}</span>}
      </div>
      <p className="llh-help-text llh-mb-0">
        Only barbell lifts get the plate calculator and bar-weight settings. The
        key can't be changed later, since it identifies the lift in every logged set.
      </p>
    </div>
  );
}

function LiftLibrary({ lifts, onChange }) {
  const [busyKey, setBusyKey] = useState(null);
  const toggle = async (l) => {
    setBusyKey(l.key);
    try { await api.put(`/admin/lifts/${l.key}`, { active: !l.active }); onChange(); }
    catch (e) { alert(errMsg(e)); }
    finally { setBusyKey(null); }
  };
  return (
    <div className="llh-card">
      <div className="llh-eyebrow llh-mb-10">Lift library</div>
      {lifts.map((l) => (
        <div key={l.key} className={l.active ? "llh-lib-row" : "llh-lib-row llh-o-55"}>
          <div className="llh-minw-0">
            <span className="llh-toggle-label">{l.name}</span>
            {!l.active && <span className="llh-tag llh-ml-6">inactive</span>}
            <div className="llh-dim-11 llh-mt-1">
              {l.key} · {l.equipment_type} · {l.tracks_weight === "none" ? "no weight" : l.tracks_weight === "optional" ? "weight optional" : "weight required"}{l.rep_unit === "seconds" ? " · timed" : ""}
            </div>
          </div>
          <button className="llh-btn-tiny" disabled={busyKey === l.key} onClick={() => toggle(l)}>
            {l.active ? "Deactivate" : "Reactivate"}
          </button>
        </div>
      ))}
      <p className="llh-help-text llh-mb-0">
        Deactivating hides a lift from the builder. Programs and history that
        already use it keep working; nothing is ever deleted.
      </p>
      <AddLiftForm onAdded={onChange} />
    </div>
  );
}

/* ── Builder ─────────────────────────────────────────────────────────────── */
function LiftRow({ l, meta, onChange, onRemove, onMove, first, last }) {
  const unit = meta && meta.rep_unit === "seconds" ? "secs" : "reps";
  const set = (k) => (e) => onChange({ ...l, [k]: e.target.value });
  return (
    <div className="llh-lift-edit-row">
      <span className="llh-lift-edit-name">{meta ? meta.name : l.lift_key}</span>
      <label className="llh-dim-11">sets
        <input type="number" min="1" max="20" className="llh-input-num llh-ml-4" value={l.sets} onChange={set("sets")} />
      </label>
      <label className="llh-dim-11">{unit}
        <input type="number" min="1" max="600" className="llh-input-num llh-ml-4" value={l.reps} onChange={set("reps")} />
      </label>
      <label className="llh-dim-11">rest s
        <input type="number" min="0" max="3600" step="15" className="llh-input-num llh-w-68 llh-ml-4" value={l.rest_seconds} onChange={set("rest_seconds")} />
      </label>
      <span className="llh-row-gap4">
        <button className="llh-btn-tiny llh-btn--dim35" disabled={first} onClick={() => onMove(-1)}>↑</button>
        <button className="llh-btn-tiny llh-btn--dim35" disabled={last} onClick={() => onMove(1)}>↓</button>
        <button className="llh-btn-tiny llh-danger" onClick={onRemove}>✕</button>
      </span>
    </div>
  );
}

function Builder({ programId, lifts, onDone }) {
  const isNew = programId == null;
  const liftMeta = {};
  lifts.forEach((l) => { liftMeta[l.key] = l; });
  const activeLifts = lifts.filter((l) => l.active);

  const [p, setP] = useState(isNew ? { name: "", workouts: [{ label: "Day A", lifts: [] }] } : null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/programs/${programId}`).then(({ data }) => {
      setP({
        name: data.name,
        workouts: data.workouts.map((w) => ({
          label: w.label,
          lifts: w.lifts.map((l) => ({ lift_key: l.key, sets: l.sets, reps: l.reps, rest_seconds: l.rest_seconds })),
        })),
      });
    }).catch((e) => setMsg({ ok: false, text: errMsg(e) }));
  }, [programId, isNew]);

  if (!p) return <div className="llh-spin" />;

  const setWorkout = (wi, w) => setP((s) => ({ ...s, workouts: s.workouts.map((x, i) => (i === wi ? w : x)) }));
  const move = (arr, i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const copy = [...arr];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  };
  const nextLabel = () => {
    const n = p.workouts.length;
    return n < 26 ? `Day ${String.fromCharCode(65 + n)}` : `Day ${n + 1}`;
  };

  const save = async () => {
    setMsg(null); setBusy(true);
    const body = {
      name: p.name,
      workouts: p.workouts.map((w) => ({
        label: w.label,
        lifts: w.lifts.map((l) => ({
          lift_key: l.lift_key,
          sets: Number(l.sets), reps: Number(l.reps), rest_seconds: Number(l.rest_seconds),
        })),
      })),
    };
    try {
      if (isNew) await api.post("/admin/programs", body);
      else await api.put(`/admin/programs/${programId}`, body);
      onDone();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e, "Could not save the program") });
      setBusy(false);
    }
  };

  return (
    <>
      <div className="llh-eyebrow">{isNew ? "New program" : "Edit program"}</div>
      <h2 className="llh-h2 llh-mb-16">
        {isNew ? "Build a program" : p.name || "Edit"}
      </h2>

      <div className="llh-card">
        <label className="llh-label">Program name</label>
        <input className="llh-input" value={p.name} onChange={(e) => setP((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. Hypertrophy Block" />
        <p className="llh-help-text llh-mb-0">
          Workouts rotate in the order below, top to bottom, one per session.
          {!isNew && " Saving replaces the whole program; assigned students see the change on their next app load."}
        </p>
      </div>

      {p.workouts.map((w, wi) => {
        const usedKeys = new Set(w.lifts.map((l) => l.lift_key));
        const addable = activeLifts.filter((l) => !usedKeys.has(l.key));
        return (
          <div key={wi} className="llh-card">
            <div className="llh-row-wrap llh-mb-8">
              <input className="llh-input llh-w-160 llh-fw-700" value={w.label}
                onChange={(e) => setWorkout(wi, { ...w, label: e.target.value })} />
              <span className="llh-flex-1" />
              <button className="llh-btn-tiny llh-btn--dim35" disabled={wi === 0}
                onClick={() => setP((s) => ({ ...s, workouts: move(s.workouts, wi, -1) }))}>↑</button>
              <button className="llh-btn-tiny llh-btn--dim35" disabled={wi === p.workouts.length - 1}
                onClick={() => setP((s) => ({ ...s, workouts: move(s.workouts, wi, 1) }))}>↓</button>
              <button className="llh-btn-tiny llh-danger llh-btn--dim35"
                disabled={p.workouts.length === 1}
                onClick={() => window.confirm(`Remove ${w.label || "this workout"}?`) && setP((s) => ({ ...s, workouts: s.workouts.filter((_, i) => i !== wi) }))}>
                Remove workout
              </button>
            </div>

            {w.lifts.length === 0 && <div className="llh-dim-13 llh-py-4">No lifts yet. Add the first one below.</div>}
            {w.lifts.map((l, li) => (
              <LiftRow key={l.lift_key} l={l} meta={liftMeta[l.lift_key]}
                first={li === 0} last={li === w.lifts.length - 1}
                onChange={(nl) => setWorkout(wi, { ...w, lifts: w.lifts.map((x, i) => (i === li ? nl : x)) })}
                onRemove={() => setWorkout(wi, { ...w, lifts: w.lifts.filter((_, i) => i !== li) })}
                onMove={(dir) => setWorkout(wi, { ...w, lifts: move(w.lifts, li, dir) })} />
            ))}

            {addable.length > 0 && (
              <div className="llh-mt-10">
                <select className="llh-input llh-h-41" value=""
                  onChange={(e) => {
                    const key = e.target.value;
                    if (!key) return;
                    const meta = liftMeta[key];
                    const reps = meta && meta.rep_unit === "seconds" ? 30 : 5;
                    setWorkout(wi, { ...w, lifts: [...w.lifts, { lift_key: key, sets: 3, reps, rest_seconds: 180 }] });
                  }}>
                  <option value="">+ Add a lift...</option>
                  {addable.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
                </select>
              </div>
            )}
          </div>
        );
      })}

      <button className="llh-btn-ghost llh-btn-ghost--sm llh-mb-14" onClick={() => setP((s) => ({ ...s, workouts: [...s.workouts, { label: nextLabel(), lifts: [] }] }))}>
        + Add workout
      </button>

      {msg && <div className={msg.ok ? "llh-fs-13 llh-c-done llh-mb-10" : "llh-fs-13 llh-c-crimson llh-mb-10"}>{msg.text}</div>}
      <div className="llh-row-gap10">
        <button className="llh-btn-primary llh-btn--dim6" disabled={busy} onClick={save}>
          {busy ? "Saving..." : isNew ? "Create program" : "Save changes"}
        </button>
        <button className="llh-btn-ghost llh-btn-ghost--sm" onClick={onDone}>Cancel</button>
      </div>
    </>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */
export default function AdminPrograms() {
  const [programs, setPrograms] = useState(null);
  const [lifts, setLifts] = useState(null);
  const [view, setView] = useState({ mode: "list" }); // or { mode: "edit", id: number|null }
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [pRes, lRes] = await Promise.all([api.get("/admin/programs"), api.get("/lifts")]);
      setPrograms(pRes.data);
      setLifts(lRes.data);
    } catch (e) { setErr(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setArchived = async (p, active) => {
    const verb = active ? "Unarchive" : "Archive";
    const warn = !active && p.student_count > 0
      ? ` ${p.student_count} student${p.student_count === 1 ? " is" : "s are"} on it and will keep training it until reassigned.`
      : "";
    if (!window.confirm(`${verb} "${p.name}"?${warn}`)) return;
    try { await api.put(`/admin/programs/${p.id}/archive`, { active }); load(); }
    catch (e) { alert(errMsg(e)); }
  };

  return (
    <div className="llh-page llh-pb-40">
      <header className="llh-header-bar">
        <Link to="/admin" className="llh-navlink"><span className="llh-nav-caret">‹</span> Admin</Link>
        <div className="llh-header-title">PROGRAMS</div>
        <span className="llh-w-60" />
      </header>

      <main className="llh-main">
        {err && <div className="llh-error-text llh-mb-12">{err}</div>}
        {(programs === null || lifts === null) && !err ? <div className="llh-spin" /> :
          programs !== null && lifts !== null && (
            view.mode === "edit" ? (
              <Builder programId={view.id} lifts={lifts} onDone={() => { setView({ mode: "list" }); load(); }} />
            ) : (
              <>
                <div className="llh-eyebrow">Custom training</div>
                <h2 className="llh-h2 llh-mb-16">Programs</h2>

                <button className="llh-btn-primary llh-mb-14" onClick={() => setView({ mode: "edit", id: null })}>+ New program</button>

                {programs.length === 0 ? (
                  <div className="llh-card llh-card--message llh-lh-15">
                    No programs yet. Students train the standard A/B rotation until
                    you build a program and assign it from their student page.
                  </div>
                ) : programs.map((p) => (
                  <div key={p.id} className={p.active ? "llh-card llh-card--tight llh-mb-10" : "llh-card llh-card--tight llh-mb-10 llh-o-60"}>
                    <div className="llh-row-top">
                      <div>
                        <div className="llh-name">
                          {p.name}
                          {!p.active && <span className="llh-badge llh-ml-8">Archived</span>}
                        </div>
                        <div className="llh-meta">
                          {p.workout_count} workout{p.workout_count === 1 ? "" : "s"} · {p.student_count} student{p.student_count === 1 ? "" : "s"} assigned
                        </div>
                      </div>
                      <div className="llh-btn-row">
                        <button className="llh-btn-ghost llh-btn-ghost--sm" onClick={() => setView({ mode: "edit", id: p.id })}>Edit</button>
                        <button className={p.active ? "llh-btn-ghost llh-btn-ghost--sm llh-danger" : "llh-btn-ghost llh-btn-ghost--sm"}
                          onClick={() => setArchived(p, !p.active)}>
                          {p.active ? "Archive" : "Unarchive"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="llh-h-10" />
                <LiftLibrary lifts={lifts} onChange={load} />
              </>
            )
          )}
      </main>
    </div>
  );
}
